import React, { useState } from 'react';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

import { Modal, Textarea, IOption, Select } from 'shared-components';
import { EventTypeConfig, ICalendlyEvent, IMessage, IRegionInterviewConfig } from 'api-paths-cas';

import { getDisplayName } from '../../../../utilities/formatting';
import { shallowEqual, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { ENROLY_AGENT_ROLES, UNI_ROLES, AGENT_ROLES, UserRole } from '../../../../store/auth/types';

export interface IProps {
  id: string;
  interviewState: string;
  interview?: ICalendlyEvent;
  interviewName?: string;
  regionInterviewConfig?: IRegionInterviewConfig[];
  eventTypes?: EventTypeConfig[];
  userRole: UserRole;
  currentInterviewId?: string;
  onSendMessage: (message: IMessage) => void;
  onSubmit: (note: string, newInterviewId?: string) => void;
  onClose: () => void;
}

const InterviewAction = (props: IProps) => {
  const {
    id,
    interviewState,
    interview,
    interviewName,
    regionInterviewConfig,
    eventTypes,
    userRole,
    currentInterviewId,
    onSendMessage,
    onSubmit,
    onClose,
  } = props;

  const { auth, config } = useSelector(({ auth, config }: ApplicationState) => ({ auth, config }), shallowEqual);

  const getMessageFrom = () => {
    if (ENROLY_AGENT_ROLES.includes(userRole)) {
      return 'enroly';
    }
    return 'uni';
  };

  const userName = auth.student_number! || 'Anonymous';
  const displayName = getDisplayName(auth, config);
  const initialMessage: IMessage = {
    message: '',
    isSendToStudent: false,
    messageType: 'note',
    createdAt: 0,
    author: userName,
    authorName: displayName || userName,
    messageFrom: getMessageFrom(),
    messageTo: 'uni',
  };

  const NONE_VALUE = 'NONE';

  const options: IOption[] = [
    {
      label: 'None',
      value: NONE_VALUE,
    },
  ];

  if (regionInterviewConfig?.length) {
    options.push({
      label: 'Recorded video interviews',
      value: 'recorded',
      populate: {
        options: regionInterviewConfig.map(interview => ({
          label: interview.interviewName || '',
          value: interview.interviewId || '',
        })),
      },
    });
  }
  if (eventTypes?.length) {
    options.push({
      label: 'Interview bookings',
      value: 'notRecorded',
      populate: {
        options: eventTypes
          .filter(item => item.id !== null)
          .map(event => ({ label: event.name ?? '', value: event.id! })),
      },
    });
  }

  const [message, setMessage] = useState({ ...initialMessage });
  const [isSendToStudent, setIsSendToStudent] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(currentInterviewId || NONE_VALUE);

  const onSelectEventType = (value: string) => {
    setSelectedEventType(value);
  };

  const cancelRequest = () => {
    if (!message) {
      return;
    }

    const interviewIdToSubmit = selectedEventType === NONE_VALUE ? undefined : selectedEventType;

    onSubmit(message.message, interviewIdToSubmit);

    const msgReason = message.message ? `: ${message.message}` : '';
    const messageType = isSendToStudent ? initialMessage.messageType : 'personalNote';
    const newMessage: IMessage = {
      ...initialMessage,
      messageType,
      messageTo: isSendToStudent ? initialMessage.messageTo : initialMessage.messageFrom,
      isSendToStudent,
      message: `Interview <${interview?.name || interviewName || ''}> ${interviewState} ${msgReason}`,
      createdAt: Date.now(),
      ...(UNI_ROLES.includes(userRole) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(userRole) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(userRole) ? { agentRead: true } : {}),
    };
    onSendMessage(newMessage);

    onClose();
  };

  return (
    <Modal
      id={id}
      show={true}
      title={'Confirm interview decision'}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={onClose}>Cancel</Button>

          <Button id="invite-to-interview-button" onClick={cancelRequest} variant={'solid'}>
            Submit
          </Button>
        </ButtonGroup>
      }
    >
      <Textarea
        id={`${id}-message`}
        value={message.message}
        label={'Add note (optional)'}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          setMessage({ ...message, message: event.currentTarget.value })
        }
        placeholder={'Type your message here...'}
        mb={3}
      />

      <Checkbox
        id={'note-to-student'}
        isChecked={isSendToStudent}
        onChange={() => setIsSendToStudent(!isSendToStudent)}
        size={'lg'}
      >
        Send this note to student
      </Checkbox>

      {id === 'resit-interview' ? (
        <Select
          id={'interview-type'}
          key={'interview-type'}
          label={'Invite to another interview?'}
          placeHolder="Select an interview..."
          options={options}
          value={selectedEventType}
          onChange={(evt: any) => onSelectEventType(evt.currentTarget.value)}
          mt={6}
        />
      ) : (
        <></>
      )}
    </Modal>
  );
};

export default InterviewAction;
